<template>
  <div>
    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(submitForm)"
      >
        <MainSection
          :categories="categoryList"
          :countries="countries"
          @category-changed="onCategoryChanged"
        />
        
        <Destination />

        <DestinationSchedulingCapacity />

        <AlertProgrammaticTarget
          class="mx-2"
          :routing-type="buyerCampaign.routing.type"
        />

        <TrafficSources :traffic-sources="allowedTrafficSources" />

        <Audience />

        <!-- Integrations is for configuring Pixels, but maybe not required for MVP: Adam -->
        <!-- <Integrations /> -->

        <Budget
          :invalid="invalid"
          :minimum-initial-deposit-satisfied="isMinimumInitialDepositRequirementSatisfied"
          :loading="isLoading"
          :selected-category="selectedCategory"
          @submit="submitForm"
        />
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import MainSection from '../campaign/MainSection.vue'
import Destination from '../campaign/Destination.vue'
import DestinationSchedulingCapacity from '../campaign/DestinationSchedulingCapacity.vue'
import TrafficSources from '../campaign/TrafficSources.vue'
import Audience from './Audience.vue'
import Budget from './Budget.vue'
import AlertProgrammaticTarget from './AlertProgrammaticTarget.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { ACCOUNT_REQUIREMENTS, dashboardNotificationForAccountRequirementsMessages } from '@/store/modules/auth.store'

export default {
  name: 'BuyerCreateCampaign',

  title: 'Create campaign',

  components: {
    MainSection,
    Destination,
    DestinationSchedulingCapacity,
    TrafficSources,
    Audience,
    Budget,
    AlertProgrammaticTarget,
  },

  props: {
    id: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      isLoading: false,
      selectedCategory: null
    }
  },

  computed: {
    ...mapState('common-entities', ['countries', 'categories']),

    ...mapGetters('account', ['allowedTrafficSources', 'allowedCategories']),

    ...mapState('campaign', ['buyerCampaign']),

    ...mapGetters('auth', ['isMinimumInitialDepositRequirementSatisfied']),

    categoryList() {
      //merge without duplicates
      const publicCategories = this.categories.filter(
        publicCategory => !this.allowedCategories.some(
          allowedCategory => allowedCategory.id === publicCategory.id
        )
      )
      const mergedCategories = this.allowedCategories.concat(publicCategories)

      return mergedCategories.sort((a, b) => a.name.localeCompare(b.name))
    },

  },

  watch: {
    isMinimumInitialDepositRequirementSatisfied: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.showDashboardNotification(dashboardNotificationForAccountRequirementsMessages[ACCOUNT_REQUIREMENTS.MinimumInitialDeposit])
        } else {
          this.hideDashboardNotification()
        }
      }
    }
  },

  async created() {
    this.setDefaultBuyerCampaign()
    await this.getCountries()
    await this.getCategories()
  },

  methods: {
    ...mapActions([
      'showDashboardNotification',
      'hideDashboardNotification'
    ]),

    ...mapActions('common-entities', ['getCountries','getCategories']),

    ...mapActions('campaign', ['setDefaultBuyerCampaign', 'createBuyerCampaign', 'fetchBuyerCampaign']),

    onCategoryChanged(category) {
      this.selectedCategory = category
    },

    async submitForm() {
      try {
        this.isLoading = true

        await this.createBuyerCampaign(this.buyerCampaign)

        // TODO: Show a detailed message about the approval process
        // and redirect to campaigns list page
        showSuccessMessage('Campaign created successfully')
        this.$router.push({ name: 'ListBuyerCampaigns' })
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
